import {createFileRoute, Navigate, useNavigate} from "@tanstack/react-router";
import {useMountedTruck} from "../../../driver/mountedTruck.ts";
import {useEffect, useMemo, useState} from "react";
import {watchLastTriplegForTruckTqo, watchTruckByIdTqo, watchTruckByPlateTqo} from "../../../rpc/webrpc-client.gen.ts";
import {TriplegStatusCodes, useTriplegStatus} from "../../../transit/transitUtils.ts";
import {usePrevious} from "@uidotdev/usehooks";
import {VenueCapsule} from "../../../venue/capsule.tsx";
import {ArriveButton} from "../../../transit/ArriveButton.tsx";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {TruckCapsule} from "../../../truck/capsule.tsx";
import {TruckMountButton} from "../../../driver/TruckMountButton.tsx";
import {useTranslation} from "react-i18next";
import {Alert, Box, Button, Divider, FormControl, FormLabel, Input as JoyInput, Stack, Typography} from "@mui/joy";
import {ToggleBar} from "../../../components/ToggleBar.tsx";
import {useNaviScratchBlockDispatch} from "../../../navi/Navi.tsx";
import {facetIcons} from "../../../components/Facet.tsx";
import {useInfallibleQuery} from "../../../util/useInfallibleQuery.tsx";
import {useRefreshableInfallibleQuery} from "../../../util/tqrefresh.tsx";
import {useBomb} from "../../../bomb/bomb.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {TruckFacetToggleSwitch} from "../../../truck/truckFacetToggleSwitch.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

const actionZoneSx = {py: 2, px: 6, fontSize: "xl"};

function TruckDrivePage() {
    const {t} = useTranslation();
    const {id: truckId} = Route.useParams();
    const mama = useMothership();
    const searchParams = Route.useSearch();
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const truckTqrv = useInfallibleQuery(
        watchTruckByIdTqo(mama, mountedTruck ? {truckId} : undefined));
    const truck = truckTqrv.data;
    const [plate, setPlate] = useState<string>("");
    const fromPlateTqrv
        = useInfallibleQuery(watchTruckByPlateTqo(mama, plate ? {plate: plate} : undefined));
    const truckFromPlate = fromPlateTqrv.data;
    const lastTriplegTqrv = useRefreshableInfallibleQuery(
        watchLastTriplegForTruckTqo(mama, mountedTruck ? {truckId: mountedTruck.id} : undefined));
    const lastTripleg = lastTriplegTqrv.data;
    const triplegStatusTqrv
        = useTriplegStatus(lastTripleg ? {tripleg: lastTripleg} : undefined);
    const lastTriplegStatus = triplegStatusTqrv.data;
    const prevTriplegStatusCode = usePrevious(lastTriplegStatus?.code);
    const navigate = useNavigate();
    const bomb = useBomb();

    const dispatchScratchNaviItem = useNaviScratchBlockDispatch();
    useEffect(() => {
        if (truck) {
            dispatchScratchNaviItem({
                to: Route.to,
                params: {id: truckId},
                icon: {icon: facetIcons.timeline},
                children: truck.displayName,
            });
        }
    }, [dispatchScratchNaviItem, truck, truckId]);

    const actionZone = useMemo(() => {
        switch (lastTriplegStatus?.code) {
            case undefined:
            case TriplegStatusCodes.UNKNOWN:
                return null;

            case TriplegStatusCodes.COMPLETE:
                return <Stack direction="column" spacing={3} sx={{alignItems: "center"}}>
                    <Stack direction="row" spacing={1.8} sx={{alignItems: "center"}}>
                        <VenueCapsule venue={lastTripleg!.destination}/>
                    </Stack>
                    <Button color="primary" sx={{px: 3, py: 1.5}}
                            onClick={() => {
                                navigate({
                                    to: "/venue/$id/service",
                                    params: {id: lastTripleg!.destination.id}
                                }).catch(bomb);
                            }}>
                        {t("driver.gotoServiceVenue")}
                    </Button>
                </Stack>;

            case TriplegStatusCodes.EN_ROUTE:
                return <Stack direction="column" spacing={3} sx={{alignItems: "center"}}>
                    <Stack direction="row" spacing={1.8} sx={{alignItems: "center"}}>
                        <VenueCapsule venue={lastTripleg!.destination}/>
                        {t("driver.enRouteTo")}
                    </Stack>
                    <ArriveButton depth="brief" truckId={truckId}
                                  sx={actionZoneSx} color="primary"/>
                </Stack>;

            case TriplegStatusCodes.AWAITING_ADMISSION:
                return <Stack direction="column" spacing={3} sx={{alignItems: "center"}}>
                    <Stack direction="row" spacing={1.8} sx={{alignItems: "center"}}>
                        <VenueCapsule venue={lastTripleg!.destination}/>
                        {t("driver.enRouteTo")}
                    </Stack>
                    <Typography level="h3" color="primary">
                        {t("driver.awaitingAdmission")}
                    </Typography>
                </Stack>;

            default:
                return <Alert color="danger" variant="soft">
                    {t("unknownError")}
                </Alert>;
        }
    }, [bomb, lastTripleg, lastTriplegStatus?.code, navigate, t, truckId]);

    useEffect(() => {
        // if we have just been admitted, switch to cargo view on the assumption that
        // the next thing the driver will want to do is unload stuff
        // 2025.07.18 that is only appropriate if we have things to unload.
        // if we are just picking up, switch to the venue service page.
        // either way, let's just let the next page handle this for us.
        if (prevTriplegStatusCode === TriplegStatusCodes.AWAITING_ADMISSION
            && lastTriplegStatus?.code === TriplegStatusCodes.COMPLETE) {
            navigate({
                to: "/driver/next",
                replace: true,
            }).catch(bomb);
        }
    }, [bomb, lastTriplegStatus?.code, navigate, prevTriplegStatusCode, searchParams, truckId]);


    if (!mountedTruckTqrv.isSuccess
        || (!!mountedTruck?.id &&
            (!lastTriplegTqrv.isSuccess ||
                (!!lastTripleg && !lastTriplegStatus)))) {
        return <SuspenseDialog message="driver truck"/>;
    }

    if (!!truckId && !!mountedTruck?.id && truckId !== mountedTruck.id) {
        return <Navigate to="/truck/$id/timeline" params={{id: truckId}}/>
    }

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>
                <ToggleBar>
                    <TruckFacetToggleSwitch dispatchValue={(next) => {
                        navigate({to: `/truck/$id/${next}`, params: {id: truckId}, replace: true})
                            .catch(bomb);
                    }} value="drive"/>
                </ToggleBar>
            </Stack>
        </PageHeader>
        <PageBody>
            <Stack direction="row" sx={{alignItems: "center", px: 4, pr: 8, py: 0.6}}>
                {mountedTruck
                    ? <>
                        <TruckCapsule truck={mountedTruck}/>
                        <Box sx={{flex: 1}}/>
                        <TruckMountButton truckId={mountedTruck.id}/>
                    </>
                    : <>
                        <FormControl>
                            <FormLabel>{t("truckPlateLabel")}</FormLabel>
                            <JoyInput value={plate}
                                      onChange={(evt) => {
                                          setPlate(evt.target.value);
                                      }}/>
                        </FormControl>
                        <Box sx={{flex: 1}}/>
                        {!!truckFromPlate && <TruckMountButton truckId={truckFromPlate.id}/>}
                    </>}
            </Stack>
            {actionZone
                ? <>
                    <Divider sx={{fontSize: "sm"}}>{t("driver.currentTriplegStatusSubheader")}</Divider>
                    <Stack direction="column" sx={{alignItems: "center", justifyContent: "center"}}>
                        {actionZone}
                    </Stack>
                </>
                : null}
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/truck/$id/drive")({
    component: TruckDrivePage,
});
